import { capitalize, get, isEmpty, orderBy } from "lodash";
import { stringifyUrl } from "query-string";
import React, { useCallback, useMemo } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { DEFAULT_DAYS_OF_WITNESSES_DATA } from ".";
import AlertMessage from "../../../../components/AlertMessage";
import DisplayLabels from "../../../../components/labels/DisplayLabels";
import { useHotspots } from "../../../../context/hotspots";
import { useTransmitScales } from "../../../../context/transmitScales";
import { avatarsApi } from "../../../../utils/constants";
import {
  ClassificationValue,
  formatHotspotName
} from "../../../../utils/hotspotUtils";
import useMapConfig, {
  WitnessesVisualizationKey,
  WitnessesVisualizationVariant
} from "../../../../utils/useMapConfig";
import TablePagination from "../../../Workspace/common/TablePagination";
import usePagination, {
  PaginationKey
} from "../../../Workspace/common/usePagination";

interface Props {}

const WitnessesTable: React.FC<Props> = () => {
  const {
    currentHotspot,
    hotspotsMap,
    hotspotWitnessesDataMap,
    getDataPeriodDescription
  } = useHotspots();
  const { pathname, search } = useLocation();
  const witnessesVisualizationKey = useMapConfig(
    (state) => state.witnessesVisualizationKey
  );
  const witnessesVisualizationVariant = useMapConfig(
    (state) => state.witnessesVisualizationVariant
  );
  const { calculatedTransmitScales } = useTransmitScales();

  const detailsPageMatch = matchPath<{
    id: string;
    section: string;
    subsection: string;
  }>(pathname, {
    path: ["/hotspots/:id/:section/:subsection", "/hotspots/:id/:section"]
  });

  const currentSectionPath = useMemo(() => {
    const {
      section,
      subsection
    }: {
      id?: string;
      section?: string;
      subsection?: string;
    } = detailsPageMatch?.params || {};

    let sectionPath = "status";
    if (section) {
      sectionPath = subsection ? `${section}/${subsection}` : section;
    }

    return sectionPath;
  }, [detailsPageMatch?.params]);

  const hotspotWitnessesData = useMemo(
    () => currentHotspot && hotspotWitnessesDataMap[currentHotspot.id],
    [currentHotspot, hotspotWitnessesDataMap]
  );

  const witnessesData = useMemo(
    () =>
      get(hotspotWitnessesDataMap, [
        detailsPageMatch?.params.id as string,
        witnessesVisualizationKey,
        witnessesVisualizationVariant
      ]) || [],
    [
      hotspotWitnessesDataMap,
      detailsPageMatch?.params.id,
      witnessesVisualizationKey,
      witnessesVisualizationVariant
    ]
  );

  const { startIndex, endIndex } = usePagination(
    useCallback(
      (state) =>
        state.getPaginationInfo(
          PaginationKey.hotspotWitnesses,
          witnessesData.length,
          search
        ),
      [witnessesData.length, search]
    )
  );

  const title = useMemo(() => {
    let text = "";

    switch (witnessesVisualizationVariant) {
      case WitnessesVisualizationVariant.all:
        text += "All " + witnessesData.length;
        break;
      case WitnessesVisualizationVariant.valid:
        text += witnessesData.length + " valid";
        break;
      case WitnessesVisualizationVariant.invalid:
        text += witnessesData.length + " invalid";
        break;
    }

    switch (witnessesVisualizationKey) {
      case WitnessesVisualizationKey.witnessedBy:
        text += " witnesses";
        break;
      case WitnessesVisualizationKey.witnessedOthers:
        text += " witnessed hotspots";
        break;
      case WitnessesVisualizationKey.bidirectional:
        text += " bidirectional witnesses";
        break;
    }

    return text;
  }, [
    witnessesData.length,
    witnessesVisualizationKey,
    witnessesVisualizationVariant
  ]);

  const description = useMemo(() => {
    if (
      !currentHotspot ||
      !(
        witnessesVisualizationKey ===
          WitnessesVisualizationKey.witnessedOthers &&
        [
          WitnessesVisualizationVariant.all,
          WitnessesVisualizationVariant.valid
        ].includes(witnessesVisualizationVariant)
      )
    ) {
      return null;
    }

    let totalCount = 0;
    let totalTransmitScale = 0;
    let totalWitnessRewardUnits = 0;

    witnessesData.forEach(
      (
        witnessData:
          | { id: string; count: number }
          | { id: string; validCount: number }
      ) => {
        const count =
          (witnessData as { count: number }).count ||
          (witnessData as { validCount: number }).validCount;

        totalTransmitScale +=
          get(calculatedTransmitScales, witnessData.id, 0) * count;

        totalWitnessRewardUnits +=
          get(hotspotsMap, [witnessData.id, "rx_reward_unit"], 0) * count;

        totalCount += count;
      }
    );

    if (totalCount === 0) {
      return null;
    }

    const weightedAverageTransmitScale = totalTransmitScale / totalCount;
    const weightedAverageWitnessRewardUnits =
      totalWitnessRewardUnits / totalCount;

    const hotspotName = formatHotspotName(currentHotspot);

    let text = `On average a transmit scale of ${weightedAverageTransmitScale.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    )} was associated with the ${totalCount} beacons that ${hotspotName} witnessed ${getDataPeriodDescription(
      currentHotspot.id,
      DEFAULT_DAYS_OF_WITNESSES_DATA
    )}.`;

    if (weightedAverageTransmitScale < 1) {
      text += ` This means that the rewards for witnessing could be increased by ${(
        (1 - weightedAverageTransmitScale) *
        100
      ).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}% if the reward scaling issues are resolved.`;
    }

    text += ` The average witness reward units (HIP15) for ${hotspotName} is ${weightedAverageWitnessRewardUnits.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    )} which is ${(
      (1 - weightedAverageWitnessRewardUnits) *
      100
    ).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}% less than the maximum witness reward units.`;

    if (weightedAverageWitnessRewardUnits < 1) {
      text += ` This means that ${hotspotName} is witnessing hotspots that are already witnessed by enough other hotspots.`;
    }

    return text;
  }, [
    calculatedTransmitScales,
    currentHotspot,
    getDataPeriodDescription,
    hotspotsMap,
    witnessesData,
    witnessesVisualizationKey,
    witnessesVisualizationVariant
  ]);

  if (!hotspotWitnessesData || isEmpty(witnessesData)) return null;

  return (
    <>
      {description && (
        <AlertMessage
          className="mb-6"
          type="info"
          title="Data analysis"
          message={description}
        />
      )}

      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 dark:border-gray-500 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
                <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      {title}
                    </th>

                    {witnessesVisualizationKey ===
                      WitnessesVisualizationKey.witnessedOthers &&
                      [
                        WitnessesVisualizationVariant.all,
                        WitnessesVisualizationVariant.valid
                      ].includes(witnessesVisualizationVariant) && (
                        <th
                          scope="col"
                          style={{ minWidth: "11rem" }}
                          className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                        >
                          Transmit Scale
                        </th>
                      )}

                    {witnessesVisualizationKey ===
                      WitnessesVisualizationKey.witnessedOthers &&
                      [
                        WitnessesVisualizationVariant.all,
                        WitnessesVisualizationVariant.valid
                      ].includes(witnessesVisualizationVariant) && (
                        <th
                          scope="col"
                          style={{ minWidth: "14rem" }}
                          className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                        >
                          Witness Reward Units
                        </th>
                      )}

                    {[
                      WitnessesVisualizationVariant.all,
                      WitnessesVisualizationVariant.valid
                    ].includes(witnessesVisualizationVariant) && (
                      <th
                        scope="col"
                        style={{ minWidth: "9rem" }}
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      >
                        Valid Count
                      </th>
                    )}

                    {[
                      WitnessesVisualizationVariant.all,
                      WitnessesVisualizationVariant.invalid
                    ].includes(witnessesVisualizationVariant) && (
                      <th
                        scope="col"
                        style={{ minWidth: "10rem" }}
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      >
                        Invalid Count
                      </th>
                    )}

                    {[
                      WitnessesVisualizationVariant.all,
                      WitnessesVisualizationVariant.invalid
                    ].includes(witnessesVisualizationVariant) && (
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                      >
                        Invalid Reasons
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500">
                  {orderBy(
                    witnessesData,
                    witnessesVisualizationVariant ===
                      WitnessesVisualizationVariant.all
                      ? "validCount"
                      : "count",
                    "desc"
                  )
                    .slice(startIndex, endIndex)
                    .map(
                      ({
                        id,
                        count = 0,
                        reasons = [],
                        validCount = 0,
                        invalidCount = 0,
                        invalidReasons = []
                      }) => {
                        const hotspot = hotspotsMap[id];

                        return (
                          <tr key={id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Link
                                className="relative flex items-center space-x-3 group"
                                to={`/hotspots/${id}/${currentSectionPath}${search}`}
                              >
                                <div className="flex-shrink-0">
                                  <img
                                    className="h-10 w-10 rounded-full"
                                    src={stringifyUrl({
                                      url: `${avatarsApi}/bottts/${id}.svg`,
                                      query: { w: 40, h: 40 }
                                    })}
                                    alt="Hotspot avatar"
                                  />
                                </div>
                                <div className="flex-1 min-w-0 focus:outline-none">
                                  <span
                                    className="absolute inset-0"
                                    aria-hidden="true"
                                  />
                                  <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                                    {hotspot ? formatHotspotName(hotspot) : id}
                                  </p>
                                  <DisplayLabels
                                    linkedUuid={id}
                                    size="small"
                                    contextWalletAddresses={[hotspot?.owner]}
                                  />
                                </div>
                              </Link>
                            </td>

                            {witnessesVisualizationKey ===
                              WitnessesVisualizationKey.witnessedOthers &&
                              [
                                WitnessesVisualizationVariant.all,
                                WitnessesVisualizationVariant.valid
                              ].includes(witnessesVisualizationVariant) && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <ClassificationValue
                                    value={calculatedTransmitScales[id] || 0}
                                  />
                                </td>
                              )}

                            {witnessesVisualizationKey ===
                              WitnessesVisualizationKey.witnessedOthers &&
                              [
                                WitnessesVisualizationVariant.all,
                                WitnessesVisualizationVariant.valid
                              ].includes(witnessesVisualizationVariant) && (
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <ClassificationValue
                                    value={get(hotspot, "rx_reward_unit", 0)}
                                    withoutTransmitScaleTooltip
                                  />
                                </td>
                              )}

                            {[
                              WitnessesVisualizationVariant.all,
                              WitnessesVisualizationVariant.valid
                            ].includes(witnessesVisualizationVariant) && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {count + validCount}
                              </td>
                            )}

                            {[
                              WitnessesVisualizationVariant.all,
                              WitnessesVisualizationVariant.invalid
                            ].includes(witnessesVisualizationVariant) && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {count + invalidCount}
                              </td>
                            )}

                            {[
                              WitnessesVisualizationVariant.all,
                              WitnessesVisualizationVariant.invalid
                            ].includes(witnessesVisualizationVariant) && (
                              <td className="px-6 py-4 whitespace-nowrap">
                                {[...reasons, ...invalidReasons].map(
                                  ({ type, count }) => (
                                    <div
                                      key={type}
                                      className="text-xs leading-5"
                                    >
                                      {`${count} x ${capitalize(
                                        type.replaceAll("_", " ")
                                      )}`}
                                    </div>
                                  )
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      }
                    )}
                </tbody>
              </table>
              <TablePagination
                paginationKey={PaginationKey.hotspotWitnesses}
                itemsCount={witnessesData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WitnessesTable;
